import { FC, ReactNode, useMemo } from 'react'
import cn from 'classnames'
import styles from 'components/atoms/inputs/InputLayout/InputLayout.styl'
import { ThemeProvider } from 'components/features/theme/ThemeProvider/ThemeProvider'
import { useInputFocus } from 'hooks/useInputFocus'
import { useFieldError } from 'hooks/useErrors'
import { animated, config, useTransition } from '@react-spring/web'
import resolveErrorById from 'constants/errors'
import Flex from 'components/atoms/Flex/Flex'

export type InputThemeStyle = 'underlined' | 'outlined' | 'bare'

export type InputLayoutProps = {
  name?: string
  label?: ReactNode
  note?: ReactNode
  units?: ReactNode
  // These values should come from hooks but can be overriden as props
  error?: ReactNode
  disabled?: boolean
  hasFocus?: boolean
  // Styles
  className?: string
  fullWidth?: boolean
  horizontal?: boolean
  wrapInLabel?: boolean
  dynamicHeight?: boolean // Keeps the errors absolutely positioned
  textInputMode?: boolean // Highlight the label when active
  themeStyle?: InputThemeStyle
}
export const InputLayout: FC<InputLayoutProps> = (props) => {
  const {
    children,
    label,
    note,
    disabled,
    name = '',
    units,
    themeStyle = 'outlined',
    className,
    horizontal,
    fullWidth = true,
    dynamicHeight = true,
    wrapInLabel = true,
    textInputMode = true,
  } = props

  // Created two refs to avoid TS error. there's got to be a better way.
  const { isFocused: labelIsFocused, rootRef: rootLabelRef } = useInputFocus<HTMLLabelElement>()
  const { isFocused: divIsFocused, rootRef: rootDivRef } = useInputFocus<HTMLDivElement>()

  const isActive = labelIsFocused || divIsFocused || props.hasFocus
  const fieldError = useFieldError(name)
  const error = fieldError || props.error
  const displayError = resolveErrorById(error as any) || error

  const noteChild = !!note && <small className={ styles.note } children={ note } />
  const unitsChild = !!units && <div className={ styles.units } children={ units } />
  const labelChild = useMemo(() => {
    if (!label) {
      return null
    }
    return wrapInLabel ? (
      <div className={ styles.label } children={ label } role='label' />
    ) : (
      <label className={ styles.label } children={ label } />
    )
  }, [label, wrapInLabel])

  const errorTransition = useTransition(displayError, {
    config: config.gentle,
    from: { transform: `translateY(0px)`, opacity: 0 },
    enter: { transform: `translateY(20px)`, opacity: 1 },
    leave: { transform: `translateY(0px)`, opacity: .5 },
  })

  const errorChild = useMemo(() => errorTransition(
    (animationStyles, animatedContent) => !!animatedContent && (
      <animated.small className={ styles.error } style={ animationStyles }>
        { animatedContent }
      </animated.small>
    )
  ), [errorTransition])

  const classNames = cn(
    className,
    styles.inputLayout,
    styles[themeStyle], {
      [styles.fitContent]: !fullWidth,
      [styles.hasError]: !!error,
      [styles.isActive]: isActive,
      [styles.disabled]: disabled,
      [styles.clickable]: wrapInLabel,
      [styles.dynamicHeight]: dynamicHeight,
      [styles.horizontal]: horizontal && !!textInputMode,
      [styles.textInputMode]: textInputMode,
    }
  )

  const content = horizontal ? (
    <>
      <Flex gap='micro'>
        { labelChild }
        { noteChild }
        { errorChild }
      </Flex>
      <div className={ cn(styles.inputWrapper, styles.horizontal) }>
        { children }
        { unitsChild }
      </div>
    </>
  ) : (
    <>
      { labelChild }
      { noteChild }
      <div className={ cn(styles.inputWrapper) }>
        { children }
        { unitsChild }
      </div>
      { errorChild }
    </>
  )

  return (
    <ThemeProvider inputActive={ isActive } inputError={ !!error } inputDisabled={ disabled }>
      {
        wrapInLabel ? (
          <label className={ classNames } ref={ rootLabelRef } children={ content } />
        ) : (
          <div className={ classNames } ref={ rootDivRef } children={ content } />
        )
      }
    </ThemeProvider>
  )
}
